import React, { useMemo } from 'react';
import { Box, Typography, Paper, Grid, LinearProgress, Card, CardContent, Chip } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat, Business, Person } from '@mui/icons-material';

interface ProgressBarsDashboardProps {
  categoryScores: Record<string, number>;
  language: string;
}

// Category name mappings
const categoryNames: Record<string, { hebrew: string; english: string }> = {
  'management': { hebrew: 'ניהול', english: 'Executive Management' },
  'organization': { hebrew: 'ארגון', english: 'Organization' },
  'repeatSales': { hebrew: 'מכירות חוזרות', english: 'Repeat Sales' },
  'finance': { hebrew: 'כספים', english: 'Finance' },
  'operations': { hebrew: 'תפעול', english: 'Operations' },
  'qualityControl': { hebrew: 'בקרת איכות', english: 'Quality Control' },
  'newSales': { hebrew: 'מכירות חדשות', english: 'New Sales' },
  'stress': { hebrew: 'מתח', english: 'Stress Management' },
  'principles': { hebrew: 'עקרונות בסיסיים', english: 'Core Principles' },
  'helpAcceptance': { hebrew: 'קבלת עזרה', english: 'Help Acceptance' },
};

const ProgressBarsDashboard: React.FC<ProgressBarsDashboardProps> = ({ categoryScores, language }) => {
  // Process category scores
  const processedScores = useMemo(() => {
    const scores = Object.entries(categoryScores).map(([_, score], index) => {
      const categoryKeys = Object.keys(categoryNames);
      const categoryKey = categoryKeys[index] || 'management';
      return {
        key: categoryKey,
        name: language === 'he'
          ? categoryNames[categoryKey]?.hebrew
          : categoryNames[categoryKey]?.english,
        value: score as number
      };
    });

    return {
      business: scores.slice(0, 7),
      personal: scores.slice(7, 10),
      businessAverage: scores.slice(0, 7).reduce((sum, cat) => sum + cat.value, 0) / 7,
      personalAverage: scores.slice(7, 10).reduce((sum, cat) => sum + cat.value, 0) / 3,
      overallAverage: scores.reduce((sum, cat) => sum + cat.value, 0) / scores.length
    };
  }, [categoryScores, language]);

  const getScoreColor = (score: number) => {
    if (score >= 80) return '#27ae60';
    if (score >= 60) return '#2ecc71';
    if (score >= 40) return '#f39c12';
    return '#e74c3c';
  };

  const getScoreStatus = (score: number) => {
    if (language === 'he') {
      if (score >= 80) return 'מצוין';
      if (score >= 60) return 'טוב';
      if (score >= 40) return 'בינוני';
      return 'טעון שיפור';
    } else {
      if (score >= 80) return 'Excellent';
      if (score >= 60) return 'Good';
      if (score >= 40) return 'Fair';
      return 'Needs Work';
    }
  };

  const getTrendIcon = (score: number) => {
    if (score >= 70) return <TrendingUp sx={{ fontSize: 20, color: '#27ae60' }} />;
    if (score >= 50) return <TrendingFlat sx={{ fontSize: 20, color: '#f39c12' }} />;
    return <TrendingDown sx={{ fontSize: 20, color: '#e74c3c' }} />;
  };

  const renderCategoryBar = (category: any) => (
    <Card
      key={category.key}
      sx={{
        mb: 2,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateX(8px)',
          boxShadow: 4
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
            {getTrendIcon(category.value)}
            <Typography variant="body1" fontWeight="600">
              {category.name}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={getScoreStatus(category.value)}
              size="small"
              sx={{
                backgroundColor: getScoreColor(category.value),
                color: 'white',
                fontWeight: 'bold',
                minWidth: '80px'
              }}
            />
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{
                color: getScoreColor(category.value),
                minWidth: '50px',
                textAlign: 'right'
              }}
            >
              {category.value.toFixed(0)}%
            </Typography>
          </Box>
        </Box>
        <Box sx={{ position: 'relative' }}>
          <LinearProgress
            variant="determinate"
            value={category.value}
            sx={{
              height: 12,
              borderRadius: 2,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: getScoreColor(category.value),
                borderRadius: 2,
                transition: 'transform 0.4s ease-in-out'
              }
            }}
          />
          {/* Reference line at 60% */}
          <Box
            sx={{
              position: 'absolute',
              left: '60%',
              top: 0,
              bottom: 0,
              width: '2px',
              backgroundColor: 'rgba(0,0,0,0.3)',
              zIndex: 1
            }}
          />
        </Box>
      </CardContent>
    </Card>
  );

  const renderSummaryCard = (title: string, icon: React.ReactNode, score: number, categories: any[]) => (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        background: `linear-gradient(135deg, ${getScoreColor(score)}22 0%, ${getScoreColor(score)}44 100%)`,
        border: `2px solid ${getScoreColor(score)}`,
        borderRadius: 3
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        {icon}
        <Typography variant="h5" fontWeight="bold">
          {title}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2 }}>
        <Typography variant="h2" fontWeight="bold" sx={{ color: getScoreColor(score) }}>
          {score.toFixed(1)}
        </Typography>
        <Typography variant="h4" color="text.secondary">%</Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={score}
        sx={{
          height: 16,
          borderRadius: 2,
          backgroundColor: '#e0e0e0',
          mb: 2,
          '& .MuiLinearProgress-bar': {
            backgroundColor: getScoreColor(score),
            borderRadius: 2
          }
        }}
      />
      <Typography variant="body2" color="text.secondary">
        {language === 'he'
          ? `${categories.length} קטגוריות`
          : `${categories.length} categories`}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          {renderSummaryCard(
            language === 'he' ? 'ממוצע עסקי' : 'Business Average',
            <Business sx={{ fontSize: 40, color: getScoreColor(processedScores.businessAverage) }} />,
            processedScores.businessAverage,
            processedScores.business
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {renderSummaryCard(
            language === 'he' ? 'ממוצע אישי' : 'Personal Average',
            <Person sx={{ fontSize: 40, color: getScoreColor(processedScores.personalAverage) }} />,
            processedScores.personalAverage,
            processedScores.personal
          )}
        </Grid>
      </Grid>

      {/* Business Categories */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Business />
          {language === 'he' ? 'קטגוריות עסקיות' : 'Business Categories'}
        </Typography>
        {processedScores.business.map(renderCategoryBar)}
      </Paper>

      {/* Personal Categories */}
      <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Person />
          {language === 'he' ? 'קטגוריות אישיות' : 'Personal Categories'}
        </Typography>
        {processedScores.personal.map(renderCategoryBar)}
      </Paper>
    </Box>
  );
};

export default ProgressBarsDashboard;
